import { missingCloudFileReference, remoteCloudFileReference, type FileReference } from './cloudFileState';
import { supabase } from './supabase';

// Disposable, account-scoped RAM cache only. Unsynced Local originals are never
// evicted. Re-login on another machine needs the manifest, not 5 GB of files.
const MAX_CACHE_BYTES = 24 * 1024 * 1024;
const files = new Map<string, { value: string; bytes: number }>();
let cacheBytes = 0;
export const clearRemoteFileCache = () => { files.clear(); cacheBytes = 0; };
supabase?.auth.onAuthStateChange(() => clearRemoteFileCache());

export async function resolveRemoteFile(value: string, signal?: AbortSignal): Promise<string> {
  if (missingCloudFileReference(value)) throw new Error('ไม่พบต้นฉบับของไฟล์นี้ กรุณาซิงค์จากเครื่องที่ยังมีไฟล์');
  const reference = remoteCloudFileReference(value);
  if (!reference) return value;
  const session = (await supabase?.auth.getSession())?.data.session;
  if (!session || !reference.key.startsWith(`${session.user.id}/`)) throw new Error('Session expired');
  const cached = files.get(reference.key);
  if (cached) { files.delete(reference.key); files.set(reference.key, cached); return cached.value; }
  const response = await fetch(`/api/files/${reference.key.split('/').map(encodeURIComponent).join('/')}`, {
    cache: 'no-store', headers: { authorization: `Bearer ${session.access_token}` },
    signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(30_000)]) : AbortSignal.timeout(30_000),
  });
  if (!response.ok) throw new Error(response.status === 404 ? 'ไฟล์ต้นฉบับบน Cloud สูญหาย กรุณากดซิงค์เพื่อตรวจสอบ' : 'ยังโหลดไฟล์จาก Cloud ไม่ได้ กรุณาตรวจอินเทอร์เน็ตแล้วลองใหม่');
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (reference.sha256) {
    const digest = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map((x) => x.toString(16).padStart(2, '0')).join('');
    if (digest !== reference.sha256) throw new Error('ไฟล์จาก Cloud ไม่ผ่านการตรวจ checksum');
  }
  const current = (await supabase?.auth.getSession())?.data.session;
  if (current?.user.id !== session.user.id) throw new Error('Session changed');
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  const result = `data:${reference.type};base64,${btoa(binary)}`;
  const entry = { value: result, bytes: result.length * 2 };
  while (cacheBytes + entry.bytes > MAX_CACHE_BYTES && files.size) {
    const key = files.keys().next().value!;
    cacheBytes -= files.get(key)!.bytes; files.delete(key);
  }
  if (entry.bytes <= MAX_CACHE_BYTES) { files.set(reference.key, entry); cacheBytes += entry.bytes; }
  return result;
}

export async function hydrateBackupFiles<T>(value: T, maxBytes: number): Promise<T> {
  let size = new Blob([JSON.stringify(value)]).size;
  const hydrated = new Map<string, string>();
  const visit = async (item: unknown): Promise<unknown> => {
    const reference: FileReference | null = remoteCloudFileReference(item);
    if (reference) {
      if (reference.size && size + reference.size * 4 / 3 > maxBytes) throw new Error('ไฟล์สำรองครบทุกไฟล์จะเกิน 100 MB ข้อมูล Cloud ยังอยู่ครบ ไม่ใช่ข้อจำกัดพื้นที่ Local');
      let data = hydrated.get(reference.key);
      if (!data) { data = await resolveRemoteFile(item as string); hydrated.set(reference.key, data); }
      size += data.length - (item as string).length;
      if (size >= maxBytes) throw new Error('ไฟล์สำรองครบทุกไฟล์เกิน 100 MB ระบบไม่ได้ส่งออกไฟล์สำรองที่ไม่ครบ');
      return data;
    }
    if (Array.isArray(item)) { const result: unknown[] = []; for (const child of item) result.push(await visit(child)); return result; }
    if (item && typeof item === 'object') { const result: Record<string, unknown> = {}; for (const [key, child] of Object.entries(item)) result[key] = await visit(child); return result; }
    return item;
  };
  return await visit(value) as T;
}
